import { BehaviorSubject, Subscription, Observable, Subject } from 'rxjs';
import { AccountService } from '../../../../../services/account.service';
import { FunnelService } from './../../../../../services/funnel.service';
import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  Input,
  SecurityContext,
  ElementRef,
} from '@angular/core';
import { IFunnelStepVariation } from '../../../../../interfaces/graph-types';
import { WatchAccountService } from '../../../../../services/watch-account.service';
import { WatchFunnelService } from '../../../../../services/watch-funnel.service';
import { TEXT_ATTRIBUTES } from '../../../../../interfaces/text-attribute/TextAttribute';
import { OnAttributeSelectArgs } from '../../../../shared/add-attribute-popup/add-attribute-popup.component';
import { DomSanitizer } from '@angular/platform-browser';
import { cloneDeep } from 'lodash';

@Component({
  selector: '[account-funnel-step-email]',
  template: require('./account-funnel-step-email.component.pug'),
  styles: [require('./account-funnel-step-email.component.css').toString()],
})
export class AccountFunnelStepEmailComponent implements OnInit {
  @Input()
  datafeed: BehaviorSubject<IFunnelStepVariation>;
  @Input()
  formDirty: boolean;
  @Input()
  baseVariation: BehaviorSubject<IFunnelStepVariation>;
  @Output()
  cancel: EventEmitter<any> = new EventEmitter();
  @Output()
  onUpdate: EventEmitter<string> = new EventEmitter();
  @Output()
  onDelete: EventEmitter<void> = new EventEmitter();
  @Output()
  onFormModified = new EventEmitter<boolean>();

  resetButton: Subject<boolean> = new Subject<boolean>();

  isSubjectAttributePopupVisible = false;
  isBodyAttributePopupVisible = false;
  areCustomTextAttributesBeingFetched = true;

  private funnelId: string = null;
  private bodyHtml: string = null;
  private bodyText: string = null;
  tooShort = false;
  private customAttributes$: Subscription;
  private subjectSelectionPosition = {
    start: 0,
    end: 0,
  };

  textAttributes = [];
  editor = null;
  editorContent = '';
  campsub: any = null;
  sub: any = null;
  info: IFunnelStepVariation;

  inputHtml: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  // -------------------------------------------------------------------
  constructor(
    private funnelService: FunnelService,
    private accountService: AccountService,
    private watchAccount: WatchAccountService,
    private watchFunnel: WatchFunnelService,
    private sanitizer: DomSanitizer,
    private elem: ElementRef,
  ) {}

  // -------------------------------------------------------------------
  ngOnInit() {
    this.sub = this.datafeed.subscribe(data => {
      this.info = cloneDeep(data || this.resetInfo());
      const safe = this.sanitizer.sanitize(
        SecurityContext.NONE, // TO FIX to prevent script execution on other machines that can see the funnel (Security audit will point that one out). But we don't have text-align and color if we sanitize it :/
        //        SecurityContext.HTML,
        this.info.body || '<div><br></div>',
      );
      this.resetButton.next(true);

      this.editorContent = safe;
      this.bodyHtml = safe;
      this.bodyText = this.info.bodyExtract;
      this.tooShort = this.bodyHtml.length < 400;
      //console.warn('datafeed set inputHtml');
      this.inputHtml.next(safe);
    });

    this.customAttributes$ = this.accountService
      .candidateAttributes(this.watchAccount.accountId().getValue())
      .subscribe(customCandidateAttributes => {
        if (!customCandidateAttributes) return;

        const customCandidateTextAttributes = customCandidateAttributes.map(
          ({ name }) => ({
            name,
            value: `candidate.custom.${name}`,
            transformed: false,
          }),
        );

        this.textAttributes = TEXT_ATTRIBUTES.map(group => {
          if (group.name === 'Candidate') {
            return {
              ...group,
              attributes: [
                ...group.attributes,
                ...customCandidateTextAttributes,
              ],
            };
          }
          return group;
        });
      });

    this.areCustomTextAttributesBeingFetched = false;
    this.campsub = this.watchFunnel.funnelId().subscribe(funnel_id => {
      this.funnelId = funnel_id;
    });
  }

  // -------------------------------------------------------------------
  ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe();
    }
    if (this.campsub) {
      this.campsub.unsubscribe();
    }
    if (this.customAttributes$) {
      this.customAttributes$.unsubscribe();
    }
  }

  // -------------------------------------------------------------------
  resetInfo() {
    this.info = {};
    this.info.continueThread = true;
    this.info.openTracking = true;
    this.info.clickTracking = true;
    this.info.asDraft = false;
    this.info.subject = '';
    this.editorContent = this.info.body;

    return this.info;
  }

  // -------------------------------------------------------------------
  getTitle() {
    return this.info.id ? 'Update step' : 'Create step';
  }

  // -------------------------------------------------------------------
  onCancel() {
    this.cancel.emit(null);
  }

  // -------------------------------------------------------------------
  onEditorCreated(editor) {
    this.editor = editor;
  }

  // -------------------------------------------------------------------
  onTextChanged({ html, text, firstUpdate }) {
    //console.warn('onTextChanged: ' + text);

    this.bodyHtml = html;
    this.tooShort = true;
    if (this.bodyHtml) this.bodyHtml.length < 400;
    this.bodyText = text;

    if (!firstUpdate) this.onFormModified.emit(true);
  }

  // -------------------------------------------------------------------
  subjectChanged(event: Event) {
    this.onFormModified.emit(true);
  }

  // -------------------------------------------------------------------
  getSubjectSelectionPosition(event: Event) {
    this.subjectSelectionPosition = {
      start: (event.target as HTMLInputElement).selectionStart,
      end: (event.target as HTMLInputElement).selectionEnd,
    };
  }

  // -------------------------------------------------------------------
  toggleSubjectAttributePopup() {
    this.isSubjectAttributePopupVisible = !this.isSubjectAttributePopupVisible;
  }

  // -------------------------------------------------------------------
  closeSubjectAttributePopup() {
    this.isSubjectAttributePopupVisible = false;
  }

  // -------------------------------------------------------------------
  handleSubjectAttributeSelect({ attribute }: OnAttributeSelectArgs) {
    const {
      start: selectionStartPosition,
      end: selectionEndPosition,
    } = this.subjectSelectionPosition;
    const newSubjectArray = this.info.subject.split('');
    const charsToInsert = `{{${attribute.value}}}`.split('');

    newSubjectArray.splice(
      selectionStartPosition,
      selectionEndPosition - selectionStartPosition,
      ...charsToInsert,
    );
    this.info.subject = newSubjectArray.join('');

    this.isSubjectAttributePopupVisible = false;
    this.onFormModified.emit(true);
  }

  // -------------------------------------------------------------------
  toggleBodyAttributePopup() {
    this.isBodyAttributePopupVisible = !this.isBodyAttributePopupVisible;
  }

  // -------------------------------------------------------------------
  closeBodyAttributePopup() {
    this.isBodyAttributePopupVisible = false;
  }

  // -------------------------------------------------------------------
  handleBodyAttributeSelect({ attribute }: OnAttributeSelectArgs) {
    const selection = this.editor.getSelection(true);

    if (attribute.transformed) {
      this.editor.insertText(selection.index, `XXX-change-XXX`);

      this.editorContent = this.bodyHtml.replace(
        'XXX-change-XXX',
        attribute.value,
      );

      //this.editorContent = attribute.value;
      // .insertText(selection.index, attribute.value);
    } else {
      this.editor.insertText(selection.index, `{{${attribute.value}}}`);
    }
    this.isBodyAttributePopupVisible = false;
  }

  // -------------------------------------------------------------------
  delete(_data: any, _onsuccess: onSmartSuccess, onfailure: onSmartFailure) {
    this.funnelService.deleteStepVariation(
      this.info.id,
      () => {
        this.onDelete.emit();
      },
      error => {
        onfailure(error);
      },
    );
  }

  // -------------------------------------------------------------------
  continueThread(_data: any, onsuccess: onSmartSuccess) {
    this.info.continueThread = !this.info.continueThread;
    this.onFormModified.emit(true);
    onsuccess();
  }

  // -------------------------------------------------------------------
  openTracking(_data: any, onsuccess: onSmartSuccess) {
    this.info.openTracking = !this.info.openTracking;
    this.onFormModified.emit(true);
    onsuccess();
  }

  // -------------------------------------------------------------------
  clickTracking(_data: any, onsuccess: onSmartSuccess) {
    this.info.clickTracking = !this.info.clickTracking;
    this.onFormModified.emit(true);
    onsuccess();
  }

  // -------------------------------------------------------------------
  asDraft(_data: any, onsuccess: onSmartSuccess) {
    this.info.asDraft = !this.info.asDraft;
    this.onFormModified.emit(true);
    onsuccess();
  }

  // -------------------------------------------------------------------
  ccChangeDetected(_data: any, newValue: any, onsuccess: onSmartSuccess) {
    this.info.cced = newValue;
    this.onFormModified.emit(true);
    onsuccess();
  }

  // -------------------------------------------------------------------
  save(_data: any, onsuccess: onSmartSuccess, onfailure: onSmartFailure) {
    if (!this.funnelId) {
      onfailure('Unknown funnel id');
      return;
    }

    let baseVariationId = null;
    if (this.baseVariation.getValue()) {
      baseVariationId = this.baseVariation.getValue().id;
    }

    // This is malicious text example
    // this.bodyHtml = 'Here is a javascript: <img src=xss onerror=alert(1)>';

    this.funnelService.createOrUpdateEmailStep(
      baseVariationId,
      this.funnelId,
      this.info.id,
      this.info.subject,
      this.bodyHtml,
      this.bodyText,
      this.info.continueThread,
      this.info.openTracking,
      this.info.clickTracking,
      this.info.asDraft,
      this.info.cced,
      data => {
        this.onUpdate.emit(data.stepVariationId);
      },
      error => {
        onfailure(error);
      },
    );
  }

  // -------------------------------------------------------------------
  deleteTagCondition(
    data: any,
    onsuccess: onSmartSuccess,
    onfailure: onSmartFailure,
  ) {
    this.funnelService.deleteConditionTags(
      [data.id],
      data => {
        this.onUpdate.emit(data.stepVariationId);
      },
      error => {
        onfailure(error);
      },
    );
  }
}
